import { IProcessStatusConfig, IReturnStatus } from "@/models/IManagement"
import { IColumnConfig } from "@/pages/Management/components/ManagementTable/types"

/**
 * 配置时间的接口
 * @param {number} day 配置时间 默认30 1(当天) 7(过去七天) 30 60 90 类推
 * @param {string} start_at_min 开始的日期
 * @param {string} end_at_max 截止的日期
 */
export interface IManagementStatisticsParam {
  day?: number
  start_at_min?: string
  end_at_max?: string
  tab?: number
  filterParam?: string
  subscribed_at_min?: string
  subscribed_at_max?: string
  /** Dashboard 等入口深链携带的 request_status（数字/字符串皆可，消费侧统一转字符串） */
  request_status?: number | string
  /** Dashboard 等入口深链携带的 request_date（数字：过去 N 天；与 day 同义） */
  request_date?: number
  pending_marking?: string
}

export interface IManagementPagination {
  current_page: number
  total: number
  limit: number
  last_page: number
  page: number
}

/**
 * 获取订阅信息列表接口
 * @param {string} search 邮箱的参数 -> 输入框的值
 * @param {string} status 订阅的状态 subscribed - unsubscribed
 * @param {IManagementStatisticsParam} customTime day和开始结束时间任意传一组
 */
export interface IManagementConfig
  extends
    IManagementStatisticsParam,
    Pick<IManagementPagination, "current_page" | "limit" | "last_page"> {
  search: string
  status: string
}

/** 退货管理列表请求与筛选栏同步的查询参数 */
export type ManagementListQueryParams = Partial<IManagementConfig> & {
  request_status?: string
  reason_ids?: string
  reasons?: string[]
  tags?: string
  solution_ids?: string
  resolution_status?: string
  shipping_methods?: string
  shipping_status?: string
  received_restocked?: string
  /** Destination 整国选中：国家码列表（CSV） */
  countries?: string
  /** Destination 部分子节点选中：地区码列表（CSV，如 US-VA） */
  region_codes?: string
  /** Dashboard 等入口深链携带的处理状态 */
  process_status?: string
  /** 订单日期筛选（与 RequestDate 结构对齐） */
  order_date?: number
  order_date_start?: string
  order_date_end?: string
  order_day?: number
  order_at_min?: string
  order_at_max?: string
  /** 退货日期筛选（与 RequestDate 结构对齐） */
  request_date?: number
  request_date_start?: string
  request_date_end?: string
  subscribed_at_min?: string
  subscribed_at_max?: string
  day?: number
  page?: number
  current_page?: number
  search?: string
  /** 排序参数：request_date:asc/request_date:desc/order_date:asc/order_date:desc */
  sort?: string
  /** @deprecated 旧的排序参数 */
  order_by?: Record<string, 1 | -1>
  /** @deprecated 旧的排序方向参数 */
  sort_direction?: string
  // 从dashboard调过来的隐藏字段
  pending_marking?: string
}

export const DEFAULT_MANAGEMENT_LIST_QUERY: ManagementListQueryParams = {
  // request_date: 30,
  limit: 10,
  page: 1,
  // search: undefined,
}

/** 详情接口 data.resent_notify_info — 重发邮件弹窗 */
export interface ResentNotifyInfo {
  customer_resent_email_type: string
  shop_resent_email_type: string
  customer_email_open: number
  shop_email_open: number
  shop_notify_email: string
  customer_email: string
}

export interface ResendNotificationParams {
  request_id: number
  email_type: string
}

export interface RecognizeLogisticsParams {
  request_id: number
  file_url: string
}

export interface RecognizeLogisticsResult {
  logistics_company?: string
  tracking_number?: string
}

export type OptPage = "next" | "pre"

export interface GetRequestIdListParams extends Partial<ManagementListQueryParams> {
  cur_id: number
  opt_page: OptPage
}

export interface GetRequestIdListResult {
  ids: number[]
}

export interface IReturnItemInfo {
  id: number
  solution_id: number
  total_quantity: string
  order_no: string
  processStatus: IProcessStatusConfig[]
  process_status: IProcessStatusConfig[]
  status: IReturnStatus
  solution_text: string
  shipping_method_id: number
  rma: string
  request_date: number
  image: string
  shipping_status: number
}

export interface IDataInfo {
  last_page: number
  current_page: number
  per_page: number
  total: number
  limit: number
  data: IReturnItemInfo[]
}

export interface QueryInfo {
  has_data: boolean
  [key: string]: any
}

export interface IReturnItem {
  data: {
    list: IDataInfo
    info: QueryInfo
  }
}

type ILabelList = {
  tracking_number: string
  shipping_carrier: string
  label_url: string
  is_label_url: number
  return_label_file: {
    type: string
    size: number
    name: string
  }
}

export interface IApproveProps {
  request_id: number
  type?: number // 1 自动 2 手动
  return_address_id?: number // 手动时不传

  return_label_type?: number
  shipping_carrier?: string // 运输商筛选框选中的名称

  tracking_number?: string // 手动填入的tn
  label_url?: string // 手动上传的pdf
  object_id?: string
  logistics_id?: number
  provider?: string // 选中的运输商
  is_label_url?: number // 1 pdf label 0 是地址
  shipping_method_id?: number // 更改的方式
  act_fee?: number // Shipping fee 金额
  return_label_file: {
    type: string
    size: number
    name: string
  }
  label_list: ILabelList[]
}

export interface ISendcloudApproveProps {
  weight: number
  sc_shipping_method_id: string
  provider: string
}

export interface IApproveParams {
  request_id: number
  return_address_id?: number // 手动时不传

  return_label_type?: number
  shipping_carrier?: string // 运输商筛选框选中的名称

  tracking_number?: string // 手动填入的tn
  label_url?: string // 手动上传的pdf
  object_id?: string
  logistics_id?: number
  provider?: string // 选中的运输商
}

export interface IRejectProps {
  request_id: number
}

export interface IExchangeProps extends IRejectProps {}

export interface IReceivedProps {
  request_id: number
  return_product_item: ReturnProductItem[]
}

export interface ReturnProductItem {
  /**
   * return product item ID
   */
  id?: number
  /**
   * 数量
   */
  quantity?: number
}

export interface IRestockProps {
  line_line_items: LineLineItem[]
  location_id: number
  request_id: number
}

export interface LineLineItem {
  id?: number
  quantity?: number
}

export interface IShoppoData {
  /**
   * 调整金额数值
   */
  adjust_val: number
  total: string
  discount: string
  tax: string
  refund: string
  max_refund: string
  refund_amount: string
  shipping: {
    money: string
    tax: string
    max: string
  }
  shipping_fee?: string
  return_fee?: string
  return_rewards?: string
  label_fee: number
  label_fee_kind: number
  estimate_label_fee: boolean
  line_return_fee?: string
  line_label_fee?: string
}
type Discount = "default" | "customize"
export interface IRefundProps {
  /**
   * 退邮费类型   1不退   2全退
   */
  refund_shipping_type: number
  discount_code_type: Discount
  /**
   * 退款金额
   */
  refund_amount: number
  /**
   * return request ID
   */
  request_id: number
  /**
   * 是否需要通知  0否  1是
   */
  is_notify?: number

  discount_code?: string
  data?: any

  /**
   * 更换的处理方式id
   */
  solution_id: number
  line_items: {
    line_item_id: number
    quantity: number
  }[]
}

export interface OrderAddressProps {
  address: string // "19455 Deerfield Avenue",
  address_detail: string // "Suite 102",
  province: string // "Virginia",
  city: string // "Lansdowne",
  zip: string // 20176",
  country_code: string // "US",
  country: string // "United States",
  province_code: string
}

export interface ChangeOrderAddressProps {
  order_address: OrderAddressProps
  request_id: number
}

// 下拉数量格式
export type ChoicesCount = {
  count: number
  value: string
  label?: string
  children?: ChoicesCount[]
}

export interface TableColumnData {
  columns: {
    name: IColumnConfig[0]["id"]
    selected: boolean
  }[]
  sort: {
    name: IColumnConfig[0]["id"]
    type: "desc" | "asc"
  }
}

export interface AvailablePickupDateData {
  time_zone: string
  available_dates: string[]
}
